import {Alert, Button, Loader, Stack} from '@mantine/core'
import {Link} from '@tanstack/react-router'
import {useState} from 'react'

import {
    addToOrderDraftKey,
    clearCreatedMark,
    clearDraft,
    DEFAULT_STORAGE_KEY,
    quoteDraftKey,
} from 'src/jobs/create-job-storage'

import {JobEntryForm} from './job-entry-form'
import type {AddToOrder} from './job-entry-logic'
import {useJobEntryPrefill} from './use-job-entry-prefill'

type JobEntryPageProps = {
    quoteId?: string | null
    order?: AddToOrder | null
    readOnly?: boolean
}

export function JobEntryPage({quoteId = null, order = null, readOnly = false}: JobEntryPageProps) {
    const storageKey = order
        ? addToOrderDraftKey(order.projectId, order.sourceJobId)
        : quoteId
          ? quoteDraftKey(quoteId)
          : DEFAULT_STORAGE_KEY
    const [session, setSession] = useState(0)

    return (
        <JobEntrySession
            key={session}
            storageKey={storageKey}
            quoteId={quoteId}
            order={order}
            readOnly={readOnly}
            onStartOver={() => {
                clearDraft(storageKey)
                clearCreatedMark(storageKey)
                setSession((count) => count + 1)
            }}
        />
    )
}

type JobEntrySessionProps = {
    storageKey: string
    quoteId: string | null
    order: AddToOrder | null
    readOnly: boolean
    onStartOver: () => void
}

function JobEntrySession({
    storageKey,
    quoteId,
    order,
    readOnly,
    onStartOver,
}: JobEntrySessionProps) {
    const prefill = useJobEntryPrefill({storageKey, quoteId, order})

    if (prefill.status === 'quote-failed') {
        return (
            <Alert color="red" title="Could not load quote">
                <Stack gap="sm" align="flex-start">
                    <Button size="compact-sm" variant="light" onClick={prefill.retry}>
                        Try again
                    </Button>
                </Stack>
            </Alert>
        )
    }
    if (prefill.status === 'loading') {
        return <Loader aria-label={prefill.label} />
    }

    return (
        <Stack gap="lg">
            {quoteId && prefill.quote && !prefill.quoteUsable ? (
                <Alert color="yellow" title="Quote cannot be converted to an order">
                    {prefill.quote.projectId
                        ? 'Already linked to an order.'
                        : 'Quote is not accepted.'}{' '}
                    <Link to="/quotes/$quoteId" params={{quoteId}}>
                        Back to quote
                    </Link>
                </Alert>
            ) : null}
            {quoteId && prefill.quoteUsable ? (
                <Alert color="blue" variant="light">
                    Filled in from the quote. Check dates, site and crew.
                </Alert>
            ) : null}
            <JobEntryForm
                start={prefill.start}
                storageKey={storageKey}
                quoteId={quoteId}
                order={order}
                readOnly={readOnly}
                onStartOver={onStartOver}
            />
        </Stack>
    )
}
